// Support notes — what an admin wrote down about this person.
//
// Was a collapsed accordion with an "Add Note" button above the list and each note's
// author, timestamp and edit control stacked in a right-hand column. Now hairline rows
// in a `SectionCard`, note body first, with add and edit as the two icon actions the
// idiom reserves for exactly that.
import { useCallback, useState } from 'react';
import { Box, IconButton, Pagination, Stack, Typography } from '../../../ui/core';
import { hairline } from '../../../ui/styles';
import { EditNoteIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters } from '../../../hooks';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { getUserNotes } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  TrailingMeta,
  Meta,
  IconAction,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';
import AddUserNoteDialog from './AddUserNoteDialog';
import EditUserNoteDialog from './EditUserNoteDialog';

const UserNotesCard = ({ user }) => {
  const dispatch = useDispatch();
  const f = useFormatters();
  // The backend returns all notes for the user as a plain array (newest first),
  // so pagination is handled client-side here.
  const userNotes = useSelector((state) => state.users.selection.userNotes);
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [notesPage, setNotesPage] = useState(1);

  const hasEditUserPermission = useHasPermission('edit_user');
  const notesPageSize = 5;
  const totalNotes = userNotes ? userNotes.length : 0;
  const notesPages = Math.ceil(totalNotes / notesPageSize);
  const pageNotes = userNotes ? userNotes.slice((notesPage - 1) * notesPageSize, notesPage * notesPageSize) : [];

  const refreshNotes = async () => {
    setNotesPage(1);
    await getUserNotes(user.id)(dispatch);
  };

  // Edits don't change the ordering, so keep the admin on their current page.
  const refreshNotesKeepPage = async () => {
    await getUserNotes(user.id)(dispatch);
  };

  const load = useCallback(() => {
    setNotesPage(1);
    return getUserNotes(user.id)(dispatch);
  }, [dispatch, user.id]);
  const [status, retry] = useSectionFetch(load);

  return (
    <>
      <SectionCard
        title="Support notes"
        icon={<EditNoteIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          <TrailingMeta>
            <Stack direction="row" alignItems="center" spacing={1}>
              {userNotes && <Meta>{totalNotes} total</Meta>}
              {hasEditUserPermission && (
                <IconAction kind="add" label="Add note" onClick={() => setIsAddNoteDialogOpen(true)} />
              )}
            </Stack>
          </TrailingMeta>
        }
      >
        {status === 'loading' && <SectionSkeleton />}
        {status === 'failed' && <SectionError onRetry={retry}>Couldn&rsquo;t load notes.</SectionError>}

        {status === 'done' && !userNotes?.length && <EmptyRows>No notes yet.</EmptyRows>}

        {status === 'done' &&
          pageNotes.map((note, i) => {
            // The backend records edits in an append-only editHistory array (newest
            // entry last); show a marker from the most recent edit so a changed note
            // is never silent.
            const lastEdit =
              Array.isArray(note.editHistory) && note.editHistory.length > 0
                ? note.editHistory[note.editHistory.length - 1]
                : null;

            return (
              <Row
                key={note.id}
                first={i === 0}
                at={f.date.toLocaleString(note.createdAt, f.date.DateFormats.DATE_SHORT)}
                label={<Box sx={{ whiteSpace: 'pre-wrap' }}>{note.body}</Box>}
                meta={
                  <>
                    {note.email || 'Unknown admin'} ·{' '}
                    {f.date.toLocaleString(note.createdAt, f.date.DateFormats.DATETIME_MED)}
                    {lastEdit && (
                      <Typography
                        component="span"
                        fontStyle="italic"
                        sx={{ fontSize: '11.5px', ml: 0.5 }}
                        title={`Edited ${f.date.toLocaleString(lastEdit.editedAt, f.date.DateFormats.DATETIME_MED)}`}
                      >
                        (edited)
                      </Typography>
                    )}
                  </>
                }
                right={
                  hasEditUserPermission && (
                    <IconButton
                      size="small"
                      aria-label="Edit note"
                      title="Edit note"
                      onClick={() => setEditingNote(note)}
                      sx={{ width: 26, height: 26, color: 'text.secondary' }}
                    >
                      <EditNoteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )
                }
              />
            );
          })}

        {notesPages > 1 && (
          <Box sx={{ px: 1.75, py: 1.25, borderTop: (t) => `1px solid ${hairline(t)}` }}>
            <Pagination count={notesPages} page={notesPage} size="small" onChange={(_, page) => setNotesPage(page)} />
          </Box>
        )}
      </SectionCard>

      <AddUserNoteDialog
        userData={user}
        isOpen={isAddNoteDialogOpen}
        onClose={() => setIsAddNoteDialogOpen(false)}
        onNoteAdded={refreshNotes}
        existingNotes={userNotes}
        notesStatus={status}
      />

      <EditUserNoteDialog
        userData={user}
        note={editingNote}
        isOpen={Boolean(editingNote)}
        onClose={() => setEditingNote(null)}
        onNoteEdited={refreshNotesKeepPage}
      />
    </>
  );
};

export default UserNotesCard;
